import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  DownloadDashboardResultsRequest,
  DownloadDashboardResultsResponse,
  RetrieveDashboardDetailRequest,
  RetrieveDashboardDetailResponse,
  RetrieveDashboardStructureRequest,
  RetrieveDashboardStructureResponse
} from './tracked-session-dashboard.types';

import {
  DOWNLOADING_DASHBOARD_RESULTS,
  RETRIEVING_DASHBOARD_DETAIL,
  RETRIEVING_DASHBOARD_STRUCTURE
} from './tracked-session-dashboard.constants';

import {
  requestDownloadDashboardResults,
  requestRetrieveDashboardDetail,
  requestRetrieveDashboardStructure
} from './tracked-session-dashboard.actions';

import {
  getDownloadDashboardResultsRequest,
  getRetrieveDashboardDetailRequest,
  getRetrieveDashboardStructureRequest
} from './tracked-session-dashboard.endpoints';

import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';

export function* performRetrieveDashboardDetailRequest({
  payload
}: SagaPayload<RetrieveDashboardDetailRequest, RetrieveDashboardDetailResponse['data']>) {
  const { requestData, onSuccess, onError } = payload;

  try {
    if (requestData.showSpinner) {
      yield put(addSpinner(RETRIEVING_DASHBOARD_DETAIL));
    }

    const [endpoint, options] = getRetrieveDashboardDetailRequest(requestData);

    const { data }: AxiosResponse<RetrieveDashboardDetailResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));

    if (onError) {
      yield call(onError, undefined);
    }
  } finally {
    yield put(removeSpinner(RETRIEVING_DASHBOARD_DETAIL));
  }
}

export function* watchForRetrieveDashboardDetailRequest() {
  yield takeLatest(requestRetrieveDashboardDetail.type, performRetrieveDashboardDetailRequest);
}

export function* performRetrieveDashboardStructureRequest({
  payload
}: SagaPayload<RetrieveDashboardStructureRequest, RetrieveDashboardStructureResponse['data']>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_DASHBOARD_STRUCTURE));

    const [endpoint, options] = getRetrieveDashboardStructureRequest(requestData);

    const { data }: AxiosResponse<RetrieveDashboardStructureResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(RETRIEVING_DASHBOARD_STRUCTURE));
  }
}

export function* watchForRetrieveDashboardStructureRequest() {
  yield takeLatest(requestRetrieveDashboardStructure.type, performRetrieveDashboardStructureRequest);
}

export function* performDownloadDashboardResultsRequest({
  payload
}: SagaPayload<DownloadDashboardResultsRequest, DownloadDashboardResultsResponse['data']>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_DASHBOARD_RESULTS));

    const [endpoint, options] = getDownloadDashboardResultsRequest(requestData);

    const { data }: AxiosResponse<DownloadDashboardResultsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_DASHBOARD_RESULTS));
  }
}

export function* watchForDownloadDashboardResultsRequest() {
  yield takeLatest(requestDownloadDashboardResults.type, performDownloadDashboardResultsRequest);
}

export default function* trackedSessionDashboardSaga() {
  yield all([
    watchForRetrieveDashboardDetailRequest(),
    watchForRetrieveDashboardStructureRequest(),
    watchForDownloadDashboardResultsRequest()
  ]);
}
